import { action } from 'typesafe-actions';
import { DashboardActionTypes, ISelectedOption } from './types';
import { CoreStatusExtended, IOffer, IRegionInterviewConfig } from 'types';
import { ITag } from '../../types';

import { IEventType } from '../event_types/types';
import { BulkActionTypeEnum } from '../studentUsers/types';
import { createAction } from '@reduxjs/toolkit';

export const switchTab = (status: string) => action(DashboardActionTypes.SWITCH_TAB, status);
export const clearStudents = () => action(DashboardActionTypes.CLEAR_STUDENTS);
export const requestFilters = () => action(DashboardActionTypes.GET_FILTERS);
export const requestStudents = (getStudentsData?: boolean) => {
  if (!getStudentsData) {
    return action(DashboardActionTypes.GET_STUDENTS);
  }
  return action(DashboardActionTypes.BULK_ACTION_SUCCESS);
};

export const requestTotals = () => action(DashboardActionTypes.GET_TOTALS);

export const requestApplications = (getApplicantsData?: boolean) => {
  if (!getApplicantsData) {
    return action(DashboardActionTypes.GET_APPLICATIONS);
  }
  return action(DashboardActionTypes.BULK_ACTION_SUCCESS);
};
export const getFiltersSuccess = (filterOptions: Record<string, any>) =>
  action(DashboardActionTypes.GET_FILTERS_SUCCESS, filterOptions);
export const getStudentsSuccess = (students: any[], auth: any, config: any) =>
  action(DashboardActionTypes.GET_STUDENTS_SUCCESS, {
    students,
    auth,
    config,
  });
export const getTotalsSuccess = (totals: any[]) =>
  action(DashboardActionTypes.GET_TOTALS_SUCCESS, {
    totals,
  });
export const getTotalsFailure = (error: any) => action(DashboardActionTypes.GET_TOTALS_FAILURE, error);
export const getAllStudentsComplete = () => action(DashboardActionTypes.GET_STUDENTS_COMPLETE);

export const addMoreStudentsSuccess = (students: any[], openStudents: any[], auth: any, config: any) =>
  action(DashboardActionTypes.ADD_MORE_STUDENTS_SUCCESS, {
    students,
    openStudents,
    auth,
    config,
  });

export const getFiltersFailure = (e: any) => action(DashboardActionTypes.GET_FILTERS_FAILURE, e);
export const getStudentsFailure = (e: any) => action(DashboardActionTypes.GET_STUDENTS_FAILURE, e);
export const updateStudent = (student: any, auth: any, config: any) =>
  action(DashboardActionTypes.UPDATE_STUDENT, { student, auth, config });
export const studentOpenedReceived = (student: any) => action(DashboardActionTypes.STUDENT_OPENED_RECEIVED, student);
export const studentClosedReceived = (student: any) => action(DashboardActionTypes.STUDENT_CLOSED_RECEIVED, student);

export const studentStatusChange = (studentId: string, status: CoreStatusExtended) =>
  action(DashboardActionTypes.STUDENT_STATUS_CHANGE, { studentId, status });

export const multiTransitionStatus = (status: string, studentIDs: Array<string>) =>
  action(DashboardActionTypes.MULTI_TRANSITION_STATUS, { status, studentIDs });
export const multiTransitionStatusSuccess = ({ status, studentIDs }: { status: string; studentIDs: Array<string> }) =>
  action(DashboardActionTypes.MULTI_TRANSITION_STATUS_SUCCESS, { status, studentIDs });
export const multiTransitionStatusFailure = (payload: any) =>
  action(DashboardActionTypes.MULTI_TRANSITION_STATUS_FAILURE, {
    studentID: payload.studentID,
  });
export const setFilters = (filters: any) => action(DashboardActionTypes.SET_FILTERS, { filters });
export const setSort = (sort: any) => action(DashboardActionTypes.SET_SORT, sort);
export const setIsFiltersVisible = (isFiltersVisible: boolean) =>
  action(DashboardActionTypes.SET_FILTERS_VISIBLE, { isFiltersVisible });

export const studentsPinTags = (
  studentsNumbers: string[],
  tags: { id: string; isProgrammable: boolean }[],
  isStudentDetailSection = false,
) =>
  action(DashboardActionTypes.STUDENTS_TAGS_PIN, {
    studentsNumbers,
    tags,
    isStudentDetailSection,
  });

export const bulkAction = (
  bulkActionType: BulkActionTypeEnum,
  data: any,
  studentNumbers?: Array<string>,
  isPartner?: boolean,
) => {
  return action(DashboardActionTypes.BULK_ACTION, {
    bulkActionType,
    data,
    studentNumbers,
    isPartner,
  });
};

export const bulkActionSuccess = () => {
  return action(DashboardActionTypes.BULK_ACTION_SUCCESS);
};
export const bulkActionFailure = (message: string) => {
  return action(DashboardActionTypes.BULK_ACTION_FAILURE, message);
};

export const selectAll = (checked: boolean) => {
  return action(DashboardActionTypes.SELECT_ALL, checked);
};

export const studentsPinTagSuccess = (payload: {
  studentsNumbers: string[];
  tags: {
    id: string;
    isProgrammable: boolean;
  }[];
  isStudentDetailSection: boolean;
}) =>
  action(DashboardActionTypes.STUDENTS_TAGS_PIN_SUCCESS, {
    ...payload,
  });
export const studentsPinTagFailure = (message: string) =>
  action(DashboardActionTypes.STUDENTS_TAGS_PIN_FAILURE, message);

export const studentsUnpinTags = (
  studentsNumbers: string[],
  tags: { id: string; isProgrammable: boolean }[],
  isStudentDetailSection = false,
) =>
  action(DashboardActionTypes.STUDENTS_TAGS_UNPIN, {
    studentsNumbers,
    isStudentDetailSection,
    tags,
  });
export const studentsUnpinTagSuccess = (payload: {
  studentsNumbers: string[];
  tags: {
    id: string;
    isProgrammable: boolean;
  }[];
  isStudentDetailSection: boolean;
}) =>
  action(DashboardActionTypes.STUDENTS_TAGS_UNPIN_SUCCESS, {
    ...payload,
  });
export const studentsUnpinTagFailure = (message: string) =>
  action(DashboardActionTypes.STUDENTS_TAGS_UNPIN_FAILURE, message);

export const studentsAddImmigrationProvider = (studentsNumbers: string[], provider: string) =>
  action(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_ADD, {
    studentsNumbers,
    provider,
  });

export const studentsAddImmigrationProviderSuccess = (data: {
  [s: string]: {
    immigrationProviders: string[];
  };
}) => action(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_ADD_SUCCESS, { data });

export const studentsAddImmigrationProviderFailure = (message: string) =>
  action(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_ADD_FAILURE, message);

export const studentsRemoveImmigrationProvider = (studentsNumbers: string[]) =>
  action(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_REMOVE, {
    studentsNumbers,
  });

export const studentsRemoveImmigrationProviderSuccess = (data: {
  [s: string]: {
    immigrationProviders: string[];
  };
}) => action(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_REMOVE_SUCCESS, { data });

export const studentsRemoveImmigrationProviderFailure = (message: string) =>
  action(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_REMOVE_FAILURE, message);

export const studentsAddServices = (studentsNumbers: string[], services: string[]) =>
  action(DashboardActionTypes.STUDENTS_SERVICES_ADD, {
    studentsNumbers,
    services,
  });

export const studentsAddServicesSuccess = (data: {
  [s: string]: {
    partners: string[];
  };
}) => action(DashboardActionTypes.STUDENTS_SERVICES_ADD_SUCCESS, { data });

export const studentsAddServicesFailure = (message: string) =>
  action(DashboardActionTypes.STUDENTS_SERVICES_ADD_FAILURE, message);

export const studentsRemoveServices = (studentsNumbers: string[], services: string[], isStudentDetailSection = false) =>
  action(DashboardActionTypes.STUDENTS_SERVICES_REMOVE, {
    studentsNumbers,
    isStudentDetailSection,
    services,
  });

export const studentsRemoveServicesSuccess = (data: {
  [s: string]: {
    partners: string[];
  };
}) => action(DashboardActionTypes.STUDENTS_SERVICES_REMOVE_SUCCESS, { data });

export const studentsRemoveServicesFailure = (message: string) =>
  action(DashboardActionTypes.STUDENTS_SERVICES_REMOVE_FAILURE, message);

export const studentAssignVisaSupport = (uni: string, studentNo: string, intakeId: string, offerId: string) =>
  action(DashboardActionTypes.STUDENT_ASSIGN_VISA_SUPPORT, {
    uni,
    studentNo,
    intakeId,
    offerId,
  });

export const studentAssignVisaSupportSuccess = (data: any) =>
  action(DashboardActionTypes.STUDENT_ASSIGN_VISA_SUPPORT_SUCCESS, { data });

export const studentAssignVisaSupportFailure = (message: string) =>
  action(DashboardActionTypes.STUDENT_ASSIGN_VISA_SUPPORT_FAILURE, message);

export const studentUnassignVisaSupport = (uni: string, studentNo: string, intakeId: string, offerId: string) =>
  action(DashboardActionTypes.STUDENT_UNASSIGN_VISA_SUPPORT, {
    uni,
    studentNo,
    intakeId,
    offerId,
  });

export const studentUnassignVisaSupportSuccess = (data: any) =>
  action(DashboardActionTypes.STUDENT_UNASSIGN_VISA_SUPPORT_SUCCESS, { data });

export const studentUnassignVisaSupportFailure = (message: string) =>
  action(DashboardActionTypes.STUDENT_UNASSIGN_VISA_SUPPORT_FAILURE, message);

export const setSelectedRows = (selectedRows: string[]) =>
  action(DashboardActionTypes.SET_SELECTED_ROWS, { selectedRows });

export const setIsDropdownDisabled = (isDisabled: boolean) =>
  action(DashboardActionTypes.SET_IS_DROPDOWN_DISABLED, { isDisabled });

export const setIsTaskBarOpen = (isOpen: boolean) => action(DashboardActionTypes.SET_IS_TASKBAR_OPEN, { isOpen });

export const setTabs = tabs => action(DashboardActionTypes.SET_TABS, { tabs });

export const requestScheduledInterview = (uni: string, students: string[], eventType: IEventType) =>
  action(DashboardActionTypes.REQUEST_SCHEDULED_INTERVIEW, { uni, students, eventType });
export const requestScheduledInterviewSuccess = (data: any) =>
  action(DashboardActionTypes.REQUEST_SCHEDULED_INTERVIEW_SUCCESS, { data });
export const requestScheduledInterviewFailure = (error: string) =>
  action(DashboardActionTypes.REQUEST_SCHEDULED_INTERVIEW_FAILURE, error);

export const cancelScheduledInterviewBulk = (uni: string, students: string[], eventType: IEventType) =>
  action(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW_BULK, {
    uni,
    students,
    eventType,
  });

export const cancelScheduledInterviewBulkSuccess = (data: any) =>
  action(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW_BULK_SUCCESS, { data });
export const cancelScheduledInterviewBulkFailure = (error: string) =>
  action(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW_BULK_FAILURE, error);

export const cancelScheduledInterview = (
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  eventTypeId: string,
  eventKey: string,
) =>
  action(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW, {
    uni,
    studentNo,
    intakeId,
    offerId,
    eventTypeId,
    eventKey,
  });

export const cancelScheduledInterviewSuccess = (data: any) =>
  action(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW_SUCCESS, { data });
export const cancelScheduledInterviewFailure = (error: string) =>
  action(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW_FAILURE, error);

export const cancelRecordedInterviewBulk = (
  uni: string,
  students: string[],
  regionInterviewConfig: IRegionInterviewConfig,
) => action(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW_BULK, { uni, students, regionInterviewConfig });
export const cancelRecordedInterviewBulkSuccess = (data: any) =>
  action(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW_BULK_SUCCESS, { data });
export const cancelRecordedInterviewBulkFailure = (error: string) =>
  action(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW_BULK_FAILURE, error);

export const cancelRecordedInterview = (
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  interviewHash: string,
) =>
  action(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW, {
    uni,
    studentNo,
    intakeId,
    offerId,
    interviewHash,
  });
export const cancelRecordedInterviewSuccess = (data: any) =>
  action(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW_SUCCESS, { data });
export const cancelRecordedInterviewFailure = (error: string) =>
  action(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW_FAILURE, error);

export const onRequestRecordedInterview = (
  uni: string,
  students: string[],
  regionInterviewConfig: IRegionInterviewConfig,
) => action(DashboardActionTypes.REQUEST_RECORDED_INTERVIEW_BULK, { uni, students, regionInterviewConfig });
export const onRequestRecordedInterviewSuccess = (offer: IOffer) =>
  action(DashboardActionTypes.REQUEST_RECORDED_INTERVIEW_BULK_SUCCESS, { offer });
export const onRequestRecordedInterviewFailure = (error: string) =>
  action(DashboardActionTypes.REQUEST_RECORDED_INTERVIEW_BULK_FAILURE, error);

export const handleOpenedStudentRecords = (data: any) => action(DashboardActionTypes.OPENED_STUDENT_RECORDS, { data });
export const triggerSITSTransfer = payload => action(DashboardActionTypes.TRIGGER_SITS_TRANSFER, payload);
export const triggerSITSTransferSuccess = (payload: any) =>
  action(DashboardActionTypes.TRIGGER_SITS_TRANSFER_SUCCESS, payload);
export const triggerSITSTransferFailure = () => action(DashboardActionTypes.TRIGGER_SITS_TRANSFER_FAILURE);

export const requestSelectedPartners = () => {
  return action(DashboardActionTypes.GET_SELECTED_PARTNERS);
};

export const getSelectedPartnersFailure = () => action(DashboardActionTypes.GET_SELECTED_PARTNERS_FAILURE);

export const getSelectedPartnersSuccess = (selectedPartnersTotals: ISelectedOption) =>
  action(DashboardActionTypes.GET_SELECTED_PARTNERS_SUCCESS, {
    selectedPartnersTotals,
  });
export const getSelectedPartnersComplete = () => action(DashboardActionTypes.GET_SELECTED_PARTNERS_COMPLETE);

export const requestSelectedTags = () => {
  return action(DashboardActionTypes.GET_SELECTED_TAGS);
};

export const getSelectedTagsFailure = (e: any) => action(DashboardActionTypes.GET_SELECTED_TAGS_FAILURE, e);

export const getSelectedTagsSuccess = (selectedTagsTotals: ISelectedOption) =>
  action(DashboardActionTypes.GET_SELECTED_TAGS_SUCCESS, {
    selectedTagsTotals,
  });
export const getSelectedTagsComplete = () => action(DashboardActionTypes.GET_SELECTED_TAGS_COMPLETE);

export const studentsPinTagsSuccess = createAction(
  DashboardActionTypes.STUDENTS_TAGS_PIN_SUCCESS,
  (
    data: {
      [s: string]: {
        tags: ITag[];
      };
    },
    isStudentDetailSection = false,
  ) => ({
    payload: {
      data,
      isStudentDetailSection,
    },
  }),
);

export const studentsPinTagsFailure = createAction<string>(DashboardActionTypes.STUDENTS_TAGS_PIN_FAILURE);

export const studentClosed = () => action(DashboardActionTypes.STUDENT_CLOSED);
